import React from 'react';
import { MetricsResponse, OrderRow, MetricDef, ORDER_METRICS, CUSTOMER_SERVICE_FAILURES_METRIC, LC_CUSTOMER_SERVICE_FAILURES_METRIC } from './logic';

export const GROUP_OPTIONS = [
  { value: 'customer', label: 'Customer' },
  { value: 'delivery_location', label: 'Delivery Location' },
  { value: 'dispatcher', label: 'Dispatcher' },
  { value: 'key_status', label: 'Key Status' },
  { value: 'lc', label: 'LC' },
  { value: 'region', label: 'Region' },
] as const;
export type MetricGroup = typeof GROUP_OPTIONS[number]['value'];
export const groupLabel = (group: MetricGroup) => GROUP_OPTIONS.find((o) => o.value === group)!.label;
export const storedGroup = (value: unknown, fallback: MetricGroup): MetricGroup =>
  GROUP_OPTIONS.some((o) => o.value === value) ? value as MetricGroup : fallback;

// Keep the original rows and drill-downs: buildView collects them into groups,
// and metric summaries sum counts/numerators/denominators, never percentages.
export function groupedOrderRows(data: MetricsResponse, group: MetricGroup): OrderRow[] {
  if (group === 'delivery_location') return data.delivery_location_rows || [];
  if (group === 'lc') return data.lc_rows || [];
  if (group === 'customer') return data.customer_rows || [];
  if (group === 'dispatcher') return data.order_rows || [];
  const rows = group === 'region' ? data.order_rows || [] : data.customer_rows || [];
  return rows.map((row) => {
    const name = (group === 'region' ? row.region : row.key_status)?.trim()
      || (group === 'region' ? '(No Region)' : '(No Key Status)');
    return { ...row, dispatcher: name, dispatcher_key: name, key_status: undefined };
  });
}

export function groupedMetric(metric: MetricDef, group: MetricGroup, page: 'lc' | 'dispatcher' = 'dispatcher'): MetricDef {
  const base = ORDER_METRICS.find((m) => m.detailKind === metric.detailKind) || metric;
  return {
    ...base,
    key: `${group}_${base.key}`,
    nameTooltip: group === 'customer'
      ? (page === 'lc' ? LC_CUSTOMER_SERVICE_FAILURES_METRIC : CUSTOMER_SERVICE_FAILURES_METRIC).nameTooltip
      : undefined,
    description: `${base.description} Grouped by ${groupLabel(group)}.`,
  };
}

interface Props {
  options: { key: string; label: string; group: string }[];
  metric: string;
  onMetricChange: (key: string) => void;
  group: MetricGroup;
  onGroupChange: (group: MetricGroup) => void;
  showGroup: boolean;
  allowDeliveryLocation?: boolean;
}
export default function OrderMetricSelector({ options, metric, onMetricChange, group, onGroupChange, showGroup, allowDeliveryLocation = false }: Props) {
  const classes = 'rounded-md border border-gray-300 dark:border-gray-600 bg-panel text-label px-3 py-1 text-xs';
  return <div className="flex items-center flex-wrap gap-4 px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
    {Array.from(new Set(options.map((o) => o.group))).map((category) => (
      <div key={category} className="flex items-center gap-1.5">
        <span className="text-[0.625rem] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">{category}</span>
        <div role="group" aria-label={`${category} metrics`} className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
          {options.filter((o) => o.group === category).map((o) => (
            <button
              key={o.key}
              type="button"
              aria-pressed={metric === o.key}
              onClick={() => onMetricChange(o.key)}
              className={`px-3 py-1 text-xs font-medium whitespace-nowrap border-r last:border-r-0 border-gray-300 dark:border-gray-600 transition-colors ${
                metric === o.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-panel text-label hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
    ))}
    {showGroup && <label className="flex items-center gap-2 text-xs text-label">
      View by
      <select className={classes} value={group} onChange={(e) => onGroupChange(e.target.value as MetricGroup)}>
        {GROUP_OPTIONS.filter(o => o.value !== 'delivery_location' || allowDeliveryLocation).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>}
  </div>;
}
